import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../api';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  Chip,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  useMediaQuery,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add,
  Delete,
  Edit,
  Save,
  Close,
  LockReset,
  Store,
  ArrowBack,
  Home,
  Person,
  AccessTime,
  Sync,
  Dashboard,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const NIVEL_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'gestor', label: 'Gestor' },
  { value: 'gerente', label: 'Gerente' },
  { value: 'subgerente', label: 'Subgerente' },
  { value: 'chefe1', label: 'Chefe I' },
  { value: 'chefe2', label: 'Chefe II' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'tecnico', label: 'Técnico' },
  { value: 'suporte', label: 'Suporte' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'visualizacao', label: 'Visualização' },
  { value: 'willians', label: 'Willians' },
];

export default function CadastroUsuarios() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { token, hasPermission } = useAuth();
  const canCreate = hasPermission('/cadastro-usuarios', 'create');
  const canUpdate = hasPermission('/cadastro-usuarios', 'update');
  const canDelete = hasPermission('/cadastro-usuarios', 'delete');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // Edição inline
  const [editRowId, setEditRowId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  // Dialog de novo usuário
  const [openNew, setOpenNew] = useState(false);
  const [newData, setNewData] = useState({
    username: '',
    nome: '',
    email: '',
    password: '',
    confirm: '',
    nivel_acesso: 'visualizacao',
    ativo: true,
  });

  // Diálogo de Alteração de Senha
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdUser, setPwdUser] = useState(null);
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  // Clientes e grupos para associação
  const [clientes, setClientes] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [clientesGrupos, setClientesGrupos] = useState([]); // Associações grupo->clientes

  // Diálogo de configuração de clientes do usuário
  const [clientesDialogOpen, setClientesDialogOpen] = useState(false);
  const [clientesDialogUser, setClientesDialogUser] = useState(null);
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(false);

  // Diálogo de configuração de cards do usuário
  const [cardsDialogOpen, setCardsDialogOpen] = useState(false);
  const [cardsDialogUser, setCardsDialogUser] = useState(null);
  const [selectedCards, setSelectedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);

  // Diálogo de detalhes do usuário com log de acesso
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailLogsLoading, setDetailLogsLoading] = useState(false);
  const [detailSyncData, setDetailSyncData] = useState([]);
  const [detailSyncLoading, setDetailSyncLoading] = useState(false);

  const headers = useMemo(
    () =>
      token
        ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        : { 'Content-Type': 'application/json' },
    [token]
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/usuarios/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Falha ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const _handleDownloadTemplate = async () => {
    try {
      const resp = await fetch(`${API_BASE}/templates/usuarios`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error('Erro ao baixar modelo');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_usuarios.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      setSnack({ open: true, message: 'Falha ao baixar modelo de usuários', severity: 'error' });
    }
  };

  // Carrega usuários apenas quando houver token para evitar 401 na primeira renderização
  useEffect(() => {
    if (token) {
      fetchUsers();
    }
  }, [token, fetchUsers]);

  // Carregar clientes e grupos
  useEffect(() => {
    if (!token) return;
    const loadData = async () => {
      try {
        const [resClientes, resGrupos, resClientesGrupos] = await Promise.all([
          fetch(`${API_BASE}/clientes/`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE}/grupos_usuarios/`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE}/clientes_grupos/`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ ok: false })),
        ]);
        if (resClientes.ok) {
          const data = await resClientes.json();
          setClientes(Array.isArray(data) ? data : []);
        }
        if (resGrupos.ok) {
          const data = await resGrupos.json();
          setGrupos(Array.isArray(data) ? data : []);
        }
        if (resClientesGrupos.ok) {
          const data = await resClientesGrupos.json();
          setClientesGrupos(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error('Erro ao carregar clientes/grupos:', e);
      }
    };
    loadData();
  }, [token]);

  // Obter clientes de um grupo
  const getClientesDoGrupo = useCallback(
    (grupoId) => {
      return clientesGrupos.filter((cg) => cg.grupo_id === grupoId).map((cg) => cg.cliente_id);
    },
    [clientesGrupos]
  );

  // Obter nome dos clientes de um usuário
  const getClientesNomesDoUsuario = useCallback(
    (user) => {
      if (!user.grupo_id) return [];
      const clienteIds = getClientesDoGrupo(user.grupo_id);
      return clientes.filter((c) => clienteIds.includes(c.id)).map((c) => c.nome);
    },
    [clientes, getClientesDoGrupo]
  );

  // Abrir dialog de clientes
  const openClientesDialog = (user) => {
    setClientesDialogUser(user);
    const clienteIds = user.grupo_id ? getClientesDoGrupo(user.grupo_id) : [];
    setSelectedClientes(clienteIds);
    setClientesDialogOpen(true);
  };

  // Salvar clientes do usuário
  const saveClientesUsuario = async () => {
    if (!clientesDialogUser) return;
    setClientesLoading(true);

    try {
      const user = clientesDialogUser;

      // Buscar ou criar um grupo para este usuário
      let grupoId = user.grupo_id;
      const grupoNome = `Grupo ${user.username}`;

      if (!grupoId) {
        // Verificar se já existe um grupo com esse nome
        const existente = grupos.find((g) => g.nome === grupoNome);
        if (existente) {
          grupoId = existente.id;
        } else {
          // Criar novo grupo
          const resGrupo = await fetch(`${API_BASE}/grupos_usuarios/`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              nome: grupoNome,
              descricao: `Grupo do usuário ${user.username}`,
              status: 'Aprovado',
            }),
          });
          if (!resGrupo.ok) throw new Error('Falha ao criar grupo');
          const novoGrupo = await resGrupo.json();
          grupoId = novoGrupo.id;
          setGrupos((prev) => [...prev, novoGrupo]);
        }

        // Associar usuário ao grupo
        await fetch(`${API_BASE}/usuarios/${user.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ grupo_id: grupoId }),
        });

        // Atualizar lista de usuários
        setRows((prev) => prev.map((r) => (r.id === user.id ? { ...r, grupo_id: grupoId } : r)));
      }

      // Remover associações antigas do grupo
      const associacoesAntigas = clientesGrupos.filter((cg) => cg.grupo_id === grupoId);
      for (const assoc of associacoesAntigas) {
        await fetch(`${API_BASE}/clientes_grupos/${assoc.id}`, {
          method: 'DELETE',
          headers,
        }).catch(() => {});
      }

      // Criar novas associações
      const novasAssociacoes = [];
      for (const clienteId of selectedClientes) {
        const res = await fetch(`${API_BASE}/clientes_grupos/`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ grupo_id: grupoId, cliente_id: clienteId }),
        });
        if (res.ok) {
          const nova = await res.json();
          novasAssociacoes.push(nova);
        }
      }

      // Atualizar estado local
      setClientesGrupos((prev) => [
        ...prev.filter((cg) => cg.grupo_id !== grupoId),
        ...novasAssociacoes,
      ]);

      setSnack({ open: true, message: 'Clientes atualizados com sucesso', severity: 'success' });
      setClientesDialogOpen(false);
    } catch (e) {
      setSnack({ open: true, message: e.message || 'Erro ao salvar clientes', severity: 'error' });
    } finally {
      setClientesLoading(false);
    }
  };

  // Lista de todos os cards disponíveis para gerenciamento
  const ALL_CARDS = [
    { key: 'Contratos', label: 'Contratos' },
    { key: 'Relatório de Obras', label: 'Relatório de Obras' },
    { key: 'Consulta Relatório de Obras', label: 'Consulta Relatório de Obras' },
    { key: 'Fluxo Cronograma', label: 'Fluxo Cronograma' },
    { key: 'Abrir Chamado', label: 'Abrir Chamado' },
    { key: 'Emergência', label: 'Emergência' },
    { key: 'Consultar Chamados', label: 'Consultar Chamados' },
    { key: 'Clientes', label: 'Clientes' },
    { key: 'Financeiro', label: 'Financeiro' },
    { key: 'Testes de Loja', label: 'Testes de Loja' },
    { key: 'Estrutura', label: 'Estrutura' },
    { key: 'Consultar OS / NFE', label: 'Consultar OS / NFE' },
    { key: 'RECEBER OS / NFE', label: 'RECEBER OS / NFE' },
    { key: 'Agenda', label: 'Agenda' },
    { key: 'Agente', label: 'Agente' },
    { key: 'Log de Acesso', label: 'Log de Acesso' },
    { key: 'Admin', label: 'Admin' },
  ];

  const normalizeCardKey = (key) => {
    if (key === 'Fluxograma') return 'Fluxo Cronograma';
    return key;
  };

  // Abrir dialog de cards
  const openCardsDialog = (user) => {
    setCardsDialogUser(user);
    let cards = [];
    try {
      cards = user.cards_permitidos
        ? typeof user.cards_permitidos === 'string'
          ? JSON.parse(user.cards_permitidos)
          : user.cards_permitidos
        : [];
    } catch (e) {
      cards = [];
    }
    const normalizedCards = Array.isArray(cards) ? cards.map(normalizeCardKey) : [];
    setSelectedCards(Array.from(new Set(normalizedCards)));
    setCardsDialogOpen(true);
  };

  // Salvar cards do usuário
  const saveCardsUsuario = async () => {
    if (!cardsDialogUser) return;
    setCardsLoading(true);
    try {
      const normalizedCards = Array.from(new Set(selectedCards.map(normalizeCardKey)));
      const res = await fetch(`${API_BASE}/usuarios/${cardsDialogUser.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ cards_permitidos: JSON.stringify(normalizedCards) }),
      });
      if (!res.ok) throw new Error('Erro ao salvar cards');
      setRows((prev) =>
        prev.map((r) =>
          r.id === cardsDialogUser.id
            ? { ...r, cards_permitidos: JSON.stringify(normalizedCards) }
            : r
        )
      );
      setSnack({ open: true, message: 'Cards atualizados com sucesso', severity: 'success' });
      setCardsDialogOpen(false);
    } catch (e) {
      setSnack({ open: true, message: e.message || 'Erro ao salvar cards', severity: 'error' });
    } finally {
      setCardsLoading(false);
    }
  };

  const openPasswordDialog = (user) => {
    setPwdUser(user);
    setPwdNew('');
    setPwdConfirm('');
    setPwdOpen(true);
  };

  const submitPasswordChange = async () => {
    if (!pwdUser) {
      setPwdOpen(false);
      return;
    }
    if (!pwdNew || !pwdConfirm) {
      setSnack({
        open: true,
        message: 'Informe a nova senha e a confirmação',
        severity: 'warning',
      });
      return;
    }
    if (pwdNew !== pwdConfirm) {
      setSnack({ open: true, message: 'Senhas não conferem', severity: 'warning' });
      return;
    }
    if (pwdNew.length < 4) {
      setSnack({
        open: true,
        message: 'Senha muito curta (mínimo 4 caracteres)',
        severity: 'warning',
      });
      return;
    }
    setPwdLoading(true);
    try {
      const payload = { password: pwdNew };
      const res = await fetch(`${API_BASE}/usuarios/${pwdUser.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = `Falha ao alterar senha (HTTP ${res.status})`;
        try {
          const j = await res.json();
          if (j?.errors) msg = Array.isArray(j.errors) ? j.errors.join(', ') : j.errors;
          else if (j?.detail) msg = j.detail;
          else if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      setSnack({ open: true, message: 'Senha alterada com sucesso', severity: 'success' });
      setPwdOpen(false);
    } catch (e) {
      setSnack({ open: true, message: e.message || 'Erro ao alterar senha', severity: 'error' });
    } finally {
      setPwdLoading(false);
    }
  };

  const startEdit = (row) => {
    setEditRowId(row.id);
    setEditDraft({ ...row, password: '' });
  };

  const cancelEdit = () => {
    setEditRowId(null);
    setEditDraft({});
  };

  const saveEdit = async () => {
    const id = editRowId;
    if (id == null) return;
    const payload = {
      username: editDraft.username || '',
      nome: editDraft.nome || '',
      email: editDraft.email || null,
      nivel_acesso: editDraft.nivel_acesso || 'visualizacao',
      ativo: !!editDraft.ativo,
      ...(editDraft.password ? { password: editDraft.password } : {}),
    };
    try {
      const res = await fetch(`${API_BASE}/usuarios/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = `Falha ao salvar (HTTP ${res.status})`;
        try {
          const j = await res.json();
          if (j?.errors) msg = Array.isArray(j.errors) ? j.errors.join(', ') : j.errors;
          else if (j?.detail) msg = j.detail;
          else if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      const updated = await res.json();
      setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
      setSnack({ open: true, message: 'Usuário atualizado', severity: 'success' });
      cancelEdit();
    } catch (e) {
      setSnack({ open: true, message: e.message || 'Erro ao salvar', severity: 'error' });
    }
  };

  const toggleAtivo = async (id, newValue) => {
    const user = rows.find((r) => r.id === id);
    if (!user) return;

    const payload = {
      username: user.username,
      nome: user.nome,
      email: user.email || null,
      nivel_acesso: user.nivel_acesso || 'visualizacao',
      ativo: newValue,
    };

    try {
      const res = await fetch(`${API_BASE}/usuarios/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = `Falha ao atualizar status (HTTP ${res.status})`;
        try {
          const j = await res.json();
          if (j?.errors) msg = Array.isArray(j.errors) ? j.errors.join(', ') : j.errors;
          else if (j?.detail) msg = j.detail;
          else if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      const updated = await res.json();
      setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
      setSnack({
        open: true,
        message: `Usuário ${newValue ? 'ativado' : 'desativado'} com sucesso`,
        severity: 'success',
      });
    } catch (e) {
      setSnack({
        open: true,
        message: e.message || 'Erro ao alterar status',
        severity: 'error',
      });
      // Recarregar para desfazer mudança visual
      fetchUsers();
    }
  };

  const toggleAgenda = async (id, field, newValue) => {
    const user = rows.find((r) => r.id === id);
    if (!user) return;
    const payload = { [field]: newValue };
    try {
      const res = await fetch(`${API_BASE}/usuarios/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } catch (e) {
      setSnack({ open: true, message: e.message || 'Erro ao alterar agenda', severity: 'error' });
      fetchUsers();
    }
  };

  const openNewDialog = () => {
    setNewData({
      username: '',
      nome: '',
      email: '',
      password: '',
      confirm: '',
      nivel_acesso: 'visualizacao',
      ativo: true,
    });
    setOpenNew(true);
  };

  const createUser = async () => {
    const { username, nome, email, password, confirm, nivel_acesso, ativo } = newData;
    if (!username || !nome || !password || !confirm) {
      setSnack({ open: true, message: 'Preencha os campos obrigatórios', severity: 'warning' });
      return;
    }
    if (password !== confirm) {
      setSnack({ open: true, message: 'Senhas não conferem', severity: 'warning' });
      return;
    }
    const payload = {
      username,
      password,
      nome,
      email: email || null,
      nivel_acesso,
      ativo: !!ativo,
    };
    try {
      const res = await fetch(`${API_BASE}/usuarios/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = `Falha ao criar (HTTP ${res.status})`;
        try {
          const j = await res.json();
          if (j?.errors) msg = Array.isArray(j.errors) ? j.errors.join(', ') : j.errors;
          else if (j?.detail) msg = j.detail;
          else if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      const created = await res.json();
      setRows((prev) => [created, ...prev]);
      setSnack({ open: true, message: 'Usuário criado', severity: 'success' });
      setOpenNew(false);
    } catch (e) {
      setSnack({ open: true, message: e.message || 'Erro ao criar', severity: 'error' });
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Remover este usuário?')) return;
    try {
      const res = await fetch(`${API_BASE}/usuarios/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(`Falha ao remover (HTTP ${res.status})`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setSnack({ open: true, message: 'Usuário removido', severity: 'success' });
    } catch (e) {
      setSnack({ open: true, message: e.message || 'Erro ao remover', severity: 'error' });
    }
  };

  const _importExcel = async (file) => {
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      // Espera colunas: username, nome, email, nivel_acesso, password, ativo
      let created = 0,
        failed = 0;
      for (const row of json) {
        const payload = {
          username: String(row.username || '').trim(),
          nome: String(row.nome || '').trim(),
          email: row.email ? String(row.email).trim() : null,
          nivel_acesso: String(row.nivel_acesso || 'visualizacao').toLowerCase(),
          password: String(row.password || '').trim(),
          ativo:
            String(row.ativo || '1')
              .toString()
              .trim()
              .toLowerCase() !== '0',
        };
        if (!payload.username || !payload.nome || !payload.password) {
          failed++;
          continue;
        }
        try {
          const res = await fetch(`${API_BASE}/usuarios/`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
          });
          if (!res.ok) throw new Error();
          const createdUser = await res.json();
          setRows((prev) => [createdUser, ...prev]);
          created++;
        } catch {
          failed++;
        }
      }
      setSnack({
        open: true,
        message: `Importação concluída: ${created} criado(s), ${failed} falha(s)`,
        severity: failed ? 'warning' : 'success',
      });
    } catch (e) {
      setSnack({ open: true, message: 'Falha ao importar Excel', severity: 'error' });
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const openDetailDialog = async (user) => {
    setDetailUser(user);
    setDetailTab(0);
    setDetailOpen(true);
    setDetailLogsLoading(true);
    setDetailSyncLoading(true);
    try {
      const res = await fetch(`${API_BASE}/access-log/user/${user.id}?limit=100`, { headers });
      if (res.ok) {
        setDetailLogs(await res.json());
      } else {
        setDetailLogs([]);
      }
    } catch {
      setDetailLogs([]);
    } finally {
      setDetailLogsLoading(false);
    }
    try {
      const res = await fetch(`${API_BASE}/agenda/sync-history/${user.id}`, { headers });
      if (res.ok) {
        setDetailSyncData(await res.json());
      } else {
        setDetailSyncData([]);
      }
    } catch {
      setDetailSyncData([]);
    } finally {
      setDetailSyncLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return (
        d.toLocaleDateString('pt-BR') +
        ' às ' +
        d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      );
    } catch {
      return dateStr;
    }
  };

  const getAcaoColor = (acao) => {
    switch (acao?.toUpperCase()) {
      case 'LOGIN':
        return 'success';
      case 'CREATE':
        return 'primary';
      case 'UPDATE':
        return 'warning';
      case 'DELETE':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1600, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
            <IconButton onClick={handleBack} size="small" aria-label="Voltar ao Dashboard">
              <ArrowBack />
            </IconButton>
            <Breadcrumbs separator="/" sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}>
              <Link
                underline="hover"
                color="inherit"
                onClick={handleBack}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <Home sx={{ mr: 0.5 }} fontSize="inherit" />
                Dashboard
              </Link>
              <Typography color="text.primary">Cadastro de Usuários</Typography>
            </Breadcrumbs>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Cadastro de Usuários
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie usuários do sistema (mesma base de login admin/admin)
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {canCreate && (
            <Button variant="contained" startIcon={<Add />} onClick={openNewDialog}>
              Novo
            </Button>
          )}
          <Button variant="text" onClick={fetchUsers}>
            Recarregar
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        /* ════ MOBILE: Cards ════ */
        <Box sx={{ mt: 1 }}>
          {rows.map((r) => {
            const nivelOpt = NIVEL_OPTIONS.find(
              (o) => o.value.toLowerCase() === String(r.nivel_acesso || '').toLowerCase()
            );
            const nomes = getClientesNomesDoUsuario(r);
            return (
              <Card key={r.id} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => openDetailDialog(r)}
                  >
                    {r.nome || r.username}
                  </Typography>
                  <Typography variant="body2">Usuário: {r.username}</Typography>
                  {r.email && <Typography variant="body2">Email: {r.email}</Typography>}
                  <Typography variant="body2">
                    Nível: {nivelOpt ? nivelOpt.label : r.nivel_acesso}
                  </Typography>
                  <Typography variant="body2">
                    Clientes:{' '}
                    {r.nivel_acesso?.toLowerCase() === 'admin' ? (
                      <Chip label="Todos" size="small" color="primary" sx={{ ml: 0.5 }} />
                    ) : nomes.length > 0 ? (
                      nomes.join(', ')
                    ) : (
                      <Chip label="Nenhum" size="small" color="warning" sx={{ ml: 0.5 }} />
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant="body2">Ativo:</Typography>
                    <Switch
                      size="small"
                      checked={!!r.ativo}
                      onChange={(e) => toggleAtivo(r.id, e.target.checked)}
                      disabled={!canUpdate}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption">Ag. Chamados</Typography>
                      <Switch
                        size="small"
                        checked={!!r.agenda_chamados}
                        onChange={(e) => toggleAgenda(r.id, 'agenda_chamados', e.target.checked)}
                        disabled={!canUpdate}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption">Relatórios</Typography>
                      <Switch
                        size="small"
                        checked={!!r.agenda_relatorios}
                        onChange={(e) => toggleAgenda(r.id, 'agenda_relatorios', e.target.checked)}
                        disabled={!canUpdate}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption">OS/NFE</Typography>
                      <Switch
                        size="small"
                        checked={!!r.agenda_recebimento}
                        onChange={(e) => toggleAgenda(r.id, 'agenda_recebimento', e.target.checked)}
                        disabled={!canUpdate}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption">Contratos</Typography>
                      <Switch
                        size="small"
                        checked={!!r.agenda_contratos}
                        onChange={(e) => toggleAgenda(r.id, 'agenda_contratos', e.target.checked)}
                        disabled={!canUpdate}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption">API RDO</Typography>
                      <Switch
                        size="small"
                        checked={!!r.agenda_diario_obra}
                        onChange={(e) => toggleAgenda(r.id, 'agenda_diario_obra', e.target.checked)}
                        disabled={!canUpdate}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                    {canUpdate && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openClientesDialog(r)}
                        title="Gerenciar clientes"
                      >
                        <Store />
                      </IconButton>
                    )}
                    {canUpdate && (
                      <IconButton
                        size="small"
                        onClick={() => openPasswordDialog(r)}
                        title="Alterar senha"
                      >
                        <LockReset />
                      </IconButton>
                    )}
                    {canUpdate && (
                      <IconButton size="small" onClick={() => startEdit(r)} title="Editar">
                        <Edit />
                      </IconButton>
                    )}
                    {canDelete && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteUser(r.id)}
                        title="Remover"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
          {!rows.length && !loading && (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Nenhum usuário encontrado
            </Typography>
          )}
        </Box>
      ) : (
        /* ════ DESKTOP: Table ════ */
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '3%', px: 0.5, whiteSpace: 'nowrap' }}>ID</TableCell>
                <TableCell sx={{ width: '8%', px: 0.5, whiteSpace: 'nowrap' }}>Usuário</TableCell>
                <TableCell sx={{ width: '10%', px: 0.5, whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ width: '12%', px: 0.5 }}>Email</TableCell>
                <TableCell sx={{ width: '6%', px: 0.5, whiteSpace: 'nowrap' }}>Nível</TableCell>
                <TableCell sx={{ width: '12%', px: 0.5 }}>Clientes</TableCell>
                <TableCell sx={{ width: '4%', px: 0.5, whiteSpace: 'nowrap' }}>Ativo</TableCell>
                <TableCell
                  align="center"
                  sx={{ fontSize: '0.7rem', width: '4%', px: 0.5, whiteSpace: 'nowrap' }}
                >
                  Ag. Cham.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontSize: '0.7rem', width: '4%', px: 0.5, whiteSpace: 'nowrap' }}
                >
                  Ag. Relat.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontSize: '0.7rem', width: '4%', px: 0.5, whiteSpace: 'nowrap' }}
                >
                  Ag. OS
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontSize: '0.7rem', width: '4%', px: 0.5, whiteSpace: 'nowrap' }}
                >
                  Ag. Cont.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontSize: '0.7rem', width: '4%', px: 0.5, whiteSpace: 'nowrap' }}
                >
                  API RDO
                </TableCell>
                <TableCell sx={{ width: '10%', px: 0.5 }}>Cards</TableCell>
                <TableCell align="right" sx={{ width: '15%', px: 0.5, whiteSpace: 'nowrap' }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ px: 1 }}>{r.id}</TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      fontSize: '0.85rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {editRowId === r.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editDraft.username || ''}
                        onChange={(e) => setEditDraft((d) => ({ ...d, username: e.target.value }))}
                      />
                    ) : (
                      <Link
                        component="button"
                        variant="body2"
                        sx={{ fontWeight: 500, cursor: 'pointer', fontSize: '0.85rem' }}
                        onClick={() => openDetailDialog(r)}
                      >
                        {r.username}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      fontSize: '0.85rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {editRowId === r.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editDraft.nome || ''}
                        onChange={(e) => setEditDraft((d) => ({ ...d, nome: e.target.value }))}
                      />
                    ) : (
                      r.nome
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      fontSize: '0.85rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {editRowId === r.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editDraft.email || ''}
                        onChange={(e) => setEditDraft((d) => ({ ...d, email: e.target.value }))}
                      />
                    ) : (
                      r.email || ''
                    )}
                  </TableCell>
                  <TableCell sx={{ px: 1, fontSize: '0.85rem' }}>
                    {editRowId === r.id ? (
                      <TextField
                        size="small"
                        select
                        fullWidth
                        value={editDraft.nivel_acesso || 'visualizacao'}
                        onChange={(e) =>
                          setEditDraft((d) => ({ ...d, nivel_acesso: e.target.value }))
                        }
                      >
                        {NIVEL_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      (() => {
                        const opt = NIVEL_OPTIONS.find(
                          (o) =>
                            o.value.toLowerCase() === String(r.nivel_acesso || '').toLowerCase()
                        );
                        return opt ? opt.label : r.nivel_acesso;
                      })()
                    )}
                  </TableCell>
                  <TableCell sx={{ px: 1, fontSize: '0.85rem' }}>
                    {(() => {
                      const nomes = getClientesNomesDoUsuario(r);
                      if (r.nivel_acesso?.toLowerCase() === 'admin') {
                        return <Chip label="Todos" size="small" color="primary" />;
                      }
                      if (nomes.length === 0) {
                        return <Chip label="Nenhum" size="small" color="warning" />;
                      }
                      return (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {nomes.slice(0, 2).map((nome, idx) => (
                            <Chip key={idx} label={nome} size="small" />
                          ))}
                          {nomes.length > 2 && (
                            <Chip label={`+${nomes.length - 2}`} size="small" variant="outlined" />
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ px: 1 }}>
                    {editRowId === r.id ? (
                      <Switch
                        size="small"
                        checked={!!editDraft.ativo}
                        onChange={(e) => setEditDraft((d) => ({ ...d, ativo: e.target.checked }))}
                      />
                    ) : (
                      <Switch
                        size="small"
                        checked={!!r.ativo}
                        onChange={(e) => toggleAtivo(r.id, e.target.checked)}
                        disabled={!canUpdate}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ px: 0.5 }}>
                    <Switch
                      size="small"
                      checked={!!r.agenda_chamados}
                      onChange={(e) => toggleAgenda(r.id, 'agenda_chamados', e.target.checked)}
                      disabled={!canUpdate}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ px: 0.5 }}>
                    <Switch
                      size="small"
                      checked={!!r.agenda_relatorios}
                      onChange={(e) => toggleAgenda(r.id, 'agenda_relatorios', e.target.checked)}
                      disabled={!canUpdate}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ px: 0.5 }}>
                    <Switch
                      size="small"
                      checked={!!r.agenda_recebimento}
                      onChange={(e) => toggleAgenda(r.id, 'agenda_recebimento', e.target.checked)}
                      disabled={!canUpdate}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ px: 0.5 }}>
                    <Switch
                      size="small"
                      checked={!!r.agenda_contratos}
                      onChange={(e) => toggleAgenda(r.id, 'agenda_contratos', e.target.checked)}
                      disabled={!canUpdate}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ px: 0.5 }}>
                    <Switch
                      size="small"
                      checked={!!r.agenda_diario_obra}
                      onChange={(e) => toggleAgenda(r.id, 'agenda_diario_obra', e.target.checked)}
                      disabled={!canUpdate}
                    />
                  </TableCell>
                  <TableCell sx={{ px: 0.5, fontSize: '0.8rem' }}>
                    {(() => {
                      if (r.nivel_acesso?.toLowerCase() === 'admin') {
                        return <Chip label="Todos" size="small" color="primary" />;
                      }
                      const cards = r.cards_permitidos
                        ? typeof r.cards_permitidos === 'string'
                          ? JSON.parse(r.cards_permitidos)
                          : r.cards_permitidos
                        : [];
                      if (!Array.isArray(cards) || cards.length === 0) {
                        return <Chip label="Padrão" size="small" variant="outlined" />;
                      }
                      return (
                        <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                          <Chip
                            label={`${cards.length} cards`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right" sx={{ px: 0.5 }}>
                    {editRowId === r.id ? (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton color="primary" onClick={saveEdit} title="Salvar">
                          <Save />
                        </IconButton>
                        <IconButton onClick={cancelEdit} title="Cancelar">
                          <Close />
                        </IconButton>
                      </Stack>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                        flexWrap="wrap"
                      >
                        {canUpdate && (
                          <IconButton
                            onClick={() => openClientesDialog(r)}
                            title="Gerenciar clientes"
                            color="primary"
                            size="small"
                          >
                            <Store fontSize="small" />
                          </IconButton>
                        )}
                        {canUpdate && (
                          <IconButton
                            onClick={() => openCardsDialog(r)}
                            title="Gerenciar cards"
                            color="info"
                            size="small"
                          >
                            <Dashboard fontSize="small" />
                          </IconButton>
                        )}
                        {canUpdate && (
                          <IconButton
                            onClick={() => openPasswordDialog(r)}
                            title="Alterar senha"
                            size="small"
                          >
                            <LockReset fontSize="small" />
                          </IconButton>
                        )}
                        {canUpdate && (
                          <IconButton onClick={() => startEdit(r)} title="Editar" size="small">
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton
                            color="error"
                            onClick={() => deleteUser(r.id)}
                            title="Remover"
                            size="small"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum usuário encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Novo Usuário */}
      <Dialog open={openNew} onClose={() => setOpenNew(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Usuário</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Usuário"
              value={newData.username}
              onChange={(e) => setNewData((d) => ({ ...d, username: e.target.value }))}
              required
            />
            <TextField
              label="Nome"
              value={newData.nome}
              onChange={(e) => setNewData((d) => ({ ...d, nome: e.target.value }))}
              required
            />
            <TextField
              label="Email"
              type="email"
              value={newData.email}
              onChange={(e) => setNewData((d) => ({ ...d, email: e.target.value }))}
            />
            <TextField
              label="Senha"
              type="password"
              value={newData.password}
              onChange={(e) => setNewData((d) => ({ ...d, password: e.target.value }))}
              required
            />
            <TextField
              label="Confirmar Senha"
              type="password"
              value={newData.confirm}
              onChange={(e) => setNewData((d) => ({ ...d, confirm: e.target.value }))}
              required
            />
            <TextField
              select
              label="Nível de acesso"
              value={newData.nivel_acesso}
              onChange={(e) => setNewData((d) => ({ ...d, nivel_acesso: e.target.value }))}
            >
              {NIVEL_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button onClick={createUser} variant="contained">
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Alterar Senha */}
      <Dialog open={pwdOpen} onClose={() => setPwdOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Alterar Senha {pwdUser ? `(${pwdUser.username})` : ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nova Senha"
              type="password"
              value={pwdNew}
              onChange={(e) => setPwdNew(e.target.value)}
              required
            />
            <TextField
              label="Confirmar Senha"
              type="password"
              value={pwdConfirm}
              onChange={(e) => setPwdConfirm(e.target.value)}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwdOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={submitPasswordChange} disabled={pwdLoading}>
            {pwdLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Selecionar Clientes */}
      <Dialog
        open={clientesDialogOpen}
        onClose={() => setClientesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Clientes do Usuário {clientesDialogUser ? `"${clientesDialogUser.username}"` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione os clientes que este usuário pode acessar:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button size="small" onClick={() => setSelectedClientes(clientes.map((c) => c.id))}>
              Selecionar Todos
            </Button>
            <Button size="small" onClick={() => setSelectedClientes([])}>
              Limpar Seleção
            </Button>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 1,
              maxHeight: 400,
              overflow: 'auto',
              p: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {clientes.map((cliente) => (
              <FormControlLabel
                key={cliente.id}
                sx={{
                  m: 0,
                  p: 0.5,
                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedClientes.includes(cliente.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedClientes((prev) => [...prev, cliente.id]);
                      } else {
                        setSelectedClientes((prev) => prev.filter((id) => id !== cliente.id));
                      }
                    }}
                  />
                }
                label={cliente.nome || `Cliente ${cliente.id}`}
              />
            ))}
            {clientes.length === 0 && (
              <Typography
                color="text.secondary"
                sx={{ gridColumn: 'span 4', textAlign: 'center', py: 2 }}
              >
                Nenhum cliente cadastrado
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {selectedClientes.length} cliente(s) selecionado(s)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientesDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveClientesUsuario} disabled={clientesLoading}>
            {clientesLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Selecionar Cards */}
      <Dialog
        open={cardsDialogOpen}
        onClose={() => setCardsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Cards do Usuário {cardsDialogUser ? `"${cardsDialogUser.username}"` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione os cards que este usuário pode visualizar no painel:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button size="small" onClick={() => setSelectedCards(ALL_CARDS.map((c) => c.key))}>
              Selecionar Todos
            </Button>
            <Button size="small" onClick={() => setSelectedCards([])}>
              Limpar Seleção
            </Button>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1,
              maxHeight: 400,
              overflow: 'auto',
              p: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {ALL_CARDS.map((card) => (
              <FormControlLabel
                key={card.key}
                sx={{
                  m: 0,
                  p: 0.5,
                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedCards.includes(card.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCards((prev) => [...prev, card.key]);
                      } else {
                        setSelectedCards((prev) => prev.filter((k) => k !== card.key));
                      }
                    }}
                  />
                }
                label={card.label}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {selectedCards.length} card(s) selecionado(s) — vazio = padrão do nível de acesso
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCardsDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveCardsUsuario} disabled={cardsLoading}>
            {cardsLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════ DIALOG: Detalhes do Usuário ════ */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person color="primary" />
            <span>{detailUser?.nome || detailUser?.username}</span>
          </Box>
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isMobile ? (
            <TextField
              select
              fullWidth
              size="small"
              value={detailTab}
              onChange={(e) => setDetailTab(Number(e.target.value))}
              sx={{ mb: 2 }}
            >
              <MenuItem value={0}>
                <Person sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                Cadastro
              </MenuItem>
              <MenuItem value={1}>
                <AccessTime sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                Log de Acesso
              </MenuItem>
              <MenuItem value={2}>
                <Sync sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                Agenda Sincronizada
              </MenuItem>
            </TextField>
          ) : (
            <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)} sx={{ mb: 2 }}>
              <Tab label="Cadastro" icon={<Person />} iconPosition="start" />
              <Tab label="Log de Acesso" icon={<AccessTime />} iconPosition="start" />
              <Tab label="Agenda Sincronizada" icon={<Sync />} iconPosition="start" />
            </Tabs>
          )}

          {detailTab === 0 && detailUser && (
            <Box>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: 150 }}>ID</TableCell>
                    <TableCell>{detailUser.id}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Usuário</TableCell>
                    <TableCell>{detailUser.username}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                    <TableCell>{detailUser.nome}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell>{detailUser.email || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nível de Acesso</TableCell>
                    <TableCell>
                      {(() => {
                        const opt = NIVEL_OPTIONS.find(
                          (o) =>
                            o.value.toLowerCase() ===
                            String(detailUser.nivel_acesso || '').toLowerCase()
                        );
                        return opt ? opt.label : detailUser.nivel_acesso;
                      })()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Clientes</TableCell>
                    <TableCell>
                      {detailUser.nivel_acesso?.toLowerCase() === 'admin' ? (
                        <Chip label="Todos" size="small" color="primary" />
                      ) : (
                        (() => {
                          const nomes = getClientesNomesDoUsuario(detailUser);
                          return nomes.length > 0 ? (
                            nomes.join(', ')
                          ) : (
                            <Chip label="Nenhum" size="small" color="warning" />
                          );
                        })()
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ativo</TableCell>
                    <TableCell>
                      <Chip
                        label={detailUser.ativo ? 'Sim' : 'Não'}
                        size="small"
                        color={detailUser.ativo ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ag. Chamados</TableCell>
                    <TableCell>
                      <Chip
                        label={detailUser.agenda_chamados ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={detailUser.agenda_chamados ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ag. Relatórios</TableCell>
                    <TableCell>
                      <Chip
                        label={detailUser.agenda_relatorios ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={detailUser.agenda_relatorios ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ag. OS/NFE</TableCell>
                    <TableCell>
                      <Chip
                        label={detailUser.agenda_recebimento ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={detailUser.agenda_recebimento ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ag. Contratos</TableCell>
                    <TableCell>
                      <Chip
                        label={detailUser.agenda_contratos ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={detailUser.agenda_contratos ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>API RDO</TableCell>
                    <TableCell>
                      <Chip
                        label={detailUser.agenda_diario_obra ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={detailUser.agenda_diario_obra ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {detailTab === 1 && (
            <>
              {detailLogsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : isMobile ? (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {detailLogs.map((log) => (
                    <Card key={log.id} sx={{ mb: 1.5, borderRadius: 2 }}>
                      <CardContent sx={{ pb: '8px !important', pt: 1.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(log.data_hora)}
                          </Typography>
                          <Chip
                            label={log.acao || 'VIEW'}
                            color={getAcaoColor(log.acao)}
                            size="small"
                          />
                        </Box>
                        <Typography variant="body2">Página: {log.pagina}</Typography>
                        <Typography variant="body2">IP: {log.ip_address || '-'}</Typography>
                      </CardContent>
                    </Card>
                  ))}
                  {detailLogs.length === 0 && (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      Nenhum registro encontrado
                    </Typography>
                  )}
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Data/Hora</TableCell>
                        <TableCell>Página</TableCell>
                        <TableCell>Ação</TableCell>
                        <TableCell>IP</TableCell>
                        <TableCell>Detalhes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailLogs.map((log) => (
                        <TableRow key={log.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDateTime(log.data_hora)}
                          </TableCell>
                          <TableCell>{log.pagina}</TableCell>
                          <TableCell>
                            <Chip
                              label={log.acao || 'VIEW'}
                              color={getAcaoColor(log.acao)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {log.ip_address || '-'}
                          </TableCell>
                          <TableCell
                            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {log.detalhes || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {detailLogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Nenhum registro encontrado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Exibindo {detailLogs.length} registros
              </Typography>
            </>
          )}

          {detailTab === 2 && (
            <>
              {detailSyncLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : detailSyncData.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  Nenhum dado de sincronização encontrado
                </Typography>
              ) : (
                <Box>
                  {detailSyncData.map((feed) => (
                    <Paper key={feed.tipo} variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Sync color={feed.ativo ? 'primary' : 'disabled'} />
                          <Typography variant="subtitle1" fontWeight="bold">
                            Agenda {feed.label}
                          </Typography>
                        </Box>
                        <Chip
                          label={feed.ativo ? 'Ativo' : 'Inativo'}
                          size="small"
                          color={feed.ativo ? 'success' : 'default'}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total de Syncs (90 dias)
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {feed.total_syncs}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Última Sincronização
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {feed.ultima_sync ? formatDateTime(feed.ultima_sync) : 'Nunca'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Último IP
                          </Typography>
                          <Typography variant="body2">{feed.ultimo_ip || '-'}</Typography>
                        </Box>
                      </Box>

                      {feed.historico && feed.historico.length > 0 && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: 'block' }}
                          >
                            Últimas sincronizações
                          </Typography>
                          {isMobile ? (
                            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                              {feed.historico.map((h) => (
                                <Box
                                  key={h.id}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    py: 0.5,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Typography variant="caption">
                                    {formatDateTime(h.data_hora)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {h.ip_address || '-'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <TableContainer sx={{ maxHeight: 200 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Data/Hora</TableCell>
                                    <TableCell>IP</TableCell>
                                    <TableCell>User Agent</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {feed.historico.map((h) => (
                                    <TableRow key={h.id} hover>
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {formatDateTime(h.data_hora)}
                                      </TableCell>
                                      <TableCell>{h.ip_address || '-'}</TableCell>
                                      <TableCell
                                        sx={{
                                          maxWidth: 250,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {h.user_agent || '-'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)} variant="outlined">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
